// simdraw.ts ↔ the Simulation::Draw orchestration (Simulation.cpp:731-900:
// FindVisibleGobs, DrawBackground, the per-layer gob loop) + the render-side
// GobMgr::FindGobs (GameObjects.cpp:2463 — the gid-map walk with structure
// dedup, edge clipping, and visibility-transition flags) + SortGobs (:2598) +
// the per-type GetSortKey/GetClippingBounds arms.
//
// Reads the PROVEN sim's state (gobm gid map, gob fields, fog bytes) and draws
// through the client stack. Presentation m_ff bits (VisibleLastFrame/
// Transitioning/IncludeFindVisible/Redraw) are MUTATED on sim gobs exactly as
// the C++ does — they are hash-stripped and save-masked (sim-quirks B16).

import type { CpuDibBitmap } from '../gfx/dibbitmap.ts';
import type { TileMap } from '../gfx/tilemap.ts';
import type { UpdateMap } from '../gfx/updatemap.ts';
import type { ClientFogDraw } from '../gfx/fogdraw.ts';
import {
  GobDrawContext,
  drawUnitGob,
  drawStructGob,
  drawWarehouseGob,
  drawSceneryGob,
  drawSurfaceDecalGob,
  drawAnimGob,
  surfaceDecalBitmapName,
  drawBuilderProgress,
  drawTankGob,
  drawTowerTurret,
  drawProcessorOverlay,
  drawMinerVacuum,
  drawReplicatorGob,
  knLayerSurfaceDecal,
  knLayerEnd,
  drawShotGob,
  shotClippingBounds,
  drawUpgradeEffect,
  drawResearchUpgradeProgress,
} from './gobdraw.ts';
import {
  kgtTankShot,
  kgtRocket,
  kgtBullet,
  kgtArtilleryShot,
  kgtAndyShot,
} from '../../../sim/src/gob/gobtypes.ts';
import { kwfGidEndMarker } from '../../../sim/src/gob/gobmgr.ts';
import { kcpgobMax } from '../../../sim/src/level.ts';
import { kstMinerSuck as kstMinerSuckClip } from '../../../sim/src/sm/message.ts';
import { kpcMax } from '../gfx/coords.ts';
import { GetColor, iclr } from '../gfx/palette.ts';
import type { Color } from '../gfx/texatlasmgr.ts';

// ht.h flag values (render-relevant)
const kfGobUnit = 0x1000;
const kfGobStructure = 0x400;
const kfGobSelected = 0x4000;
const kfGobBeingBuilt = 0x10;
const kfGobBeingUpgraded = 0x8;
const kfGobRedraw = 0x2;
const kfGobVisibleLastFrame = 0x10000;
const kfGobIncludeFindVisible = 0x20000;
const kfGobTransitioningToVisible = 0x40000;
const kfGobLayerMask = 0x01f00000;
const kfGobLayerSurfaceDecal = 0x100000;
const kfUnitDrawHealthIndicator = 0x0002;
const kfUntcHasFullnessIndicator = 0x0020;
const kcyHealthBar = 4;
const kgidNull = 0xffff;

// gob types the dispatcher routes structurally
const kgtSurfaceDecal = 4;
const kgtScenery = 5;
const kgtAnimation = 6;
const kgtScorch = 28;
const kgtSmoke = 29;
const kgtPuff = 30;

export interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

// ht.h:6035
const MakeSortKey = (a: number, b: number): number => (a << 8) | ((b >> 2) & 0xff) | 0;

interface RenderGob {
  GetType(): number;
  GetFlags(): number;
  SetFlags(ff: number): void;
  GetId(): number;
  m_wx: number;
  m_wy: number;
  m_gidNext?: number;
}

/** The sim surface simdraw consumes (a Sim instance's ctx + level slices). */
export interface SimView {
  gobm: {
    gidMapForRender(): { map: Uint16Array; ctx: number; cty: number };
    GetGob(gid: number, fActiveOnly?: boolean): unknown;
  };
  fogBytes: Uint8Array; // fogm.GetMapPtr()
  ctxMap: number; // fog/terrain map tile dims
  ctyMap: number;
  trMapBytes: Uint8Array; // terrain map bytes (walls)
  wxView: number; // Simulation m_wxView/m_wyView
  wyView: number;
  /** gpplrLocal + gapuntc — the builder-progress arm's gating + cost lookups. */
  plrLocal: { GetSide(): number; GetHandicap(): number } | null;
  apuntc: { GetArmorStrength(): number }[];
}

// game/Unit.cpp:512-522 + GameObjects.cpp:366/799 — per-type sort keys
export function gobSortKey(ctx: GobDrawContext, pgob: RenderGob): number {
  const g = pgob as RenderGob & {
    m_puntc?: { wdySortOffset: number };
    m_szBitmap?: string | null;
  };
  if (pgob.GetFlags() & kfGobUnit) {
    return MakeSortKey(pgob.m_wy + (g.m_puntc?.wdySortOffset ?? 0), pgob.GetId());
  }
  if (pgob.GetType() === kgtScenery && g.m_szBitmap) {
    const tbm = ctx.tbmFor(g.m_szBitmap);
    const baseline = tbm === null ? 0 : ctx.coords.WcFromPc(tbm.GetBaseline());
    return MakeSortKey(pgob.m_wy + baseline, pgob.GetId());
  }
  // Gob::GetSortKey (GameObjects.cpp:366) — plain world-y
  return MakeSortKey(pgob.m_wy, pgob.GetId());
}

// game/GameObjects.cpp:2598-2636 — insertion sort on the key
export function SortGobs(ctx: GobDrawContext, apgob: RenderGob[]): void {
  if (apgob.length <= 1) return;
  const keys = apgob.map((g) => gobSortKey(ctx, g));
  for (let i = 1; i < apgob.length; i++) {
    for (let j = i; j > 0 && keys[j - 1] > keys[j]; j--) {
      const kT = keys[j];
      keys[j] = keys[j - 1];
      keys[j - 1] = kT;
      const gT = apgob[j];
      apgob[j] = apgob[j - 1];
      apgob[j - 1] = gT;
    }
  }
}

// per-type clipping bounds (Unit.cpp:524-561, GameObjects.cpp:384/644/804/931)
export function gobClippingBounds(ctx: GobDrawContext, pgob: RenderGob): Rect {
  const c = ctx.coords;
  const g = pgob as RenderGob & {
    m_ani?: { GetStrip(): number; GetFrame(): number; GetData(): { name: string } | null };
    m_puntc?: {
      szAniName: string;
      wf: number;
      wrcUIBounds: { left: number; top: number; right: number; bottom: number };
    };
    m_wfUnit?: number;
    m_szBitmap?: string | null;
  };
  const ff = pgob.GetFlags();

  // TankShotGob/RocketGob (+ Bullet/ArtilleryShot/AndyShot via inheritance):
  // perf-gated animation bbox (Tank.cpp:733-751, LRInfantry.cpp:268-285)
  const gtShot = pgob.GetType();
  if (
    gtShot === kgtTankShot ||
    gtShot === kgtBullet ||
    gtShot === kgtArtilleryShot ||
    gtShot === kgtAndyShot ||
    gtShot === kgtRocket
  ) {
    return shotClippingBounds(ctx, pgob as never, gtShot === kgtRocket);
  }

  if (ff & kfGobUnit) {
    // UnitGob::GetClippingBounds — animation bbox ∪ selection chrome
    const anid = ctx.anidFor(g.m_puntc!.szAniName);
    const prc: Rect =
      anid === null
        ? { left: 0, top: 0, right: 0, bottom: 0 }
        : anid.GetBounds(g.m_ani!.GetStrip(), g.m_ani!.GetFrame());

    // TankGob/TowerGob::GetClippingBounds (Tank.cpp:237-247, Tower.cpp:414-424):
    // union the turret bbox at the base's special point (gob-relative here; the
    // view offset lands with the shared offset below)
    const gTurret = pgob as RenderGob & {
      m_aniTurret?: { GetStrip(): number; GetFrame(): number };
      m_aniOverlay?: unknown;
      m_ani?: { GetSpecialPoint?(): { x: number; y: number } };
    };
    if (
      anid !== null &&
      gTurret.m_aniTurret !== undefined &&
      gTurret.m_ani?.GetSpecialPoint !== undefined
    ) {
      const rcTurret = anid.GetBounds(
        gTurret.m_aniTurret.GetStrip(),
        gTurret.m_aniTurret.GetFrame(),
      );
      const pt = gTurret.m_ani.GetSpecialPoint();
      rcTurret.left += pt.x;
      rcTurret.right += pt.x;
      rcTurret.top += pt.y;
      rcTurret.bottom += pt.y;
      prc.left = Math.min(prc.left, rcTurret.left);
      prc.top = Math.min(prc.top, rcTurret.top);
      prc.right = Math.max(prc.right, rcTurret.right);
      prc.bottom = Math.max(prc.bottom, rcTurret.bottom);
    }

    // MinerGob::GetClippingBounds (Miner.cpp:205-216): union the vacuum bbox
    // while sucking (strip = body dir16 / 2, set at draw)
    const gMiner = pgob as RenderGob & {
      m_aniVacuum?: { GetFrame(): number };
      m_st?: number;
    };
    if (gMiner.m_aniVacuum !== undefined && gMiner.m_st === kstMinerSuckClip) {
      const anidVac = ctx.anidFor('vacuum.anir');
      if (anidVac !== null) {
        const rcVac = anidVac.GetBounds(g.m_ani!.GetStrip() >> 1, gMiner.m_aniVacuum.GetFrame());
        prc.left = Math.min(prc.left, rcVac.left);
        prc.top = Math.min(prc.top, rcVac.top);
        prc.right = Math.max(prc.right, rcVac.right);
        prc.bottom = Math.max(prc.bottom, rcVac.bottom);
      }
    }

    // ProcessorGob::GetClippingBounds (Processor.cpp:86-99): union the docked
    // fake miner's bbox (strip 4, frame by approach side, in ABSOLUTE pixels)
    const gProc = pgob as RenderGob & {
      m_aniOverlay?: unknown;
      m_wptFakeMiner?: { wx: number; wy: number };
    };
    if (
      anid !== null &&
      gProc.m_aniOverlay !== undefined &&
      gProc.m_wptFakeMiner !== undefined &&
      gProc.m_wptFakeMiner.wx !== -1000
    ) {
      const kwcTileClip = 256;
      const ifrm = gProc.m_wptFakeMiner.wy < pgob.m_wy + kwcTileClip ? 1 : 0;
      const rcMiner = anid.GetBounds(4, ifrm);
      // absolute pixels: subtract the gob offset added below
      const dxm = c.PcFromUwc(gProc.m_wptFakeMiner.wx) - c.PcFromUwc(pgob.m_wx);
      const dym = c.PcFromWc(gProc.m_wptFakeMiner.wy) - c.PcFromUwc(pgob.m_wy);
      prc.left = Math.min(prc.left, rcMiner.left + dxm);
      prc.top = Math.min(prc.top, rcMiner.top + dym);
      prc.right = Math.max(prc.right, rcMiner.right + dxm);
      prc.bottom = Math.max(prc.bottom, rcMiner.bottom + dym);
    }

    // ReplicatorGob::GetClippingBounds (Replicator.cpp:113-134) REPLACES the
    // base method: the union of the two quadrant strips (frame 0), the plain
    // selection UI bounds (no health-bar/fullness chrome — the C++ comments
    // the kcyHealthBar adjust out), then the gob offset — return directly.
    const gRepl = pgob as RenderGob & { m_fReplicating?: boolean };
    if (anid !== null && gRepl.m_fReplicating !== undefined) {
      const rc0 = anid.GetBounds(0, 0);
      const rc1 = anid.GetBounds(1, 0);
      const rcR: Rect = {
        left: Math.min(rc0.left, rc1.left),
        top: Math.min(rc0.top, rc1.top),
        right: Math.max(rc0.right, rc1.right),
        bottom: Math.max(rc0.bottom, rc1.bottom),
      };
      if (ff & kfGobSelected) {
        // kfPerfSelectionBrackets pinned ON
        rcR.left = Math.min(rcR.left, c.PcFromWc(g.m_puntc!.wrcUIBounds.left));
        rcR.top = Math.min(rcR.top, c.PcFromWc(g.m_puntc!.wrcUIBounds.top));
        rcR.right = Math.max(rcR.right, c.PcFromWc(g.m_puntc!.wrcUIBounds.right));
        rcR.bottom = Math.max(rcR.bottom, c.PcFromWc(g.m_puntc!.wrcUIBounds.bottom));
      }
      rcR.left += c.PcFromUwc(pgob.m_wx);
      rcR.right += c.PcFromUwc(pgob.m_wx);
      rcR.top += c.PcFromUwc(pgob.m_wy);
      rcR.bottom += c.PcFromUwc(pgob.m_wy);
      return rcR;
    }

    // BuilderGob::GetClippingBounds (Builder.cpp:330-342): widen to the UI
    // bounds while the build-progress bar shows (ShowingBuildProgress's
    // ally/handicap gate is host state the render pass approximates with the
    // queue check, as drawBuilderProgress does)
    const gBldr = pgob as RenderGob & { m_bq?: { IsEmpty(): boolean } };
    if (gBldr.m_bq !== undefined && !gBldr.m_bq.IsEmpty()) {
      const rcUI = {
        left: c.PcFromWc(g.m_puntc!.wrcUIBounds.left),
        right: c.PcFromWc(g.m_puntc!.wrcUIBounds.right),
      };
      prc.left = Math.min(prc.left, rcUI.left);
      prc.right = Math.max(prc.right, rcUI.right);
    }
    const rcSel: Rect = {
      left: c.PcFromWc(g.m_puntc!.wrcUIBounds.left),
      top: c.PcFromWc(g.m_puntc!.wrcUIBounds.top),
      right: c.PcFromWc(g.m_puntc!.wrcUIBounds.right),
      bottom: c.PcFromWc(g.m_puntc!.wrcUIBounds.bottom),
    };
    const kcyFullness = c.PcFromWc(32) + 2; // kwcFullnessPip = 2/16 tile (ht.h:6567)
    if (ff & kfGobSelected) {
      // kfPerfSelectionBrackets pinned ON: union with the bracket box
      rcSel.top -= kcyHealthBar;
      if (g.m_puntc!.wf & kfUntcHasFullnessIndicator) rcSel.bottom += kcyFullness;
      prc.left = Math.min(prc.left, rcSel.left);
      prc.top = Math.min(prc.top, rcSel.top);
      prc.right = Math.max(prc.right, rcSel.right);
      prc.bottom = Math.max(prc.bottom, rcSel.bottom);
    } else if (
      ff & (kfGobBeingUpgraded | kfGobBeingBuilt) ||
      (g.m_wfUnit ?? 0) & kfUnitDrawHealthIndicator
    ) {
      prc.top = rcSel.top - kcyHealthBar;
      prc.left = Math.min(prc.left, rcSel.left);
      prc.right = Math.max(prc.right, rcSel.right);
    }
    prc.left += c.PcFromUwc(pgob.m_wx);
    prc.right += c.PcFromUwc(pgob.m_wx);
    prc.top += c.PcFromUwc(pgob.m_wy);
    prc.bottom += c.PcFromUwc(pgob.m_wy);
    return prc;
  }

  if (pgob.GetType() === kgtScenery && g.m_szBitmap) {
    const tbm = ctx.tbmFor(g.m_szBitmap);
    const siz = tbm === null ? { cx: 0, cy: 0 } : tbm.GetSize();
    const left = c.PcFromUwc(pgob.m_wx);
    const top = c.PcFromUwc(pgob.m_wy);
    return { left, top, right: left + siz.cx, bottom: top + siz.cy };
  }

  // SurfaceDecalGob::GetClippingBounds (GameObjects.cpp:644-654) — INHERITED by
  // ScorchGob (ht.h:6351), so both types take this arm (as dispatchDraw below);
  // the scorch's bitmap resolves through gaptbmScorches by m_nScorch
  if (pgob.GetType() === kgtSurfaceDecal || pgob.GetType() === kgtScorch) {
    const szBitmap = surfaceDecalBitmapName(g as { m_szBitmap: string | null; m_nScorch?: number });
    const tbm = szBitmap === null ? null : ctx.tbmFor(szBitmap);
    const siz = tbm === null ? { cx: 0, cy: 0 } : tbm.GetSize();
    const left = c.PcFromUwc(pgob.m_wx) - ((siz.cx - 1) >> 1);
    const top = c.PcFromUwc(pgob.m_wy) - (siz.cy - 1);
    return { left, top, right: left + siz.cx, bottom: top + siz.cy };
  }

  if (g.m_ani !== undefined) {
    // AnimGob::GetClippingBounds
    const data = g.m_ani.GetData();
    const anid = data === null ? null : ctx.anidFor(`${data.name}.anir`);
    const prc: Rect =
      anid === null
        ? { left: 0, top: 0, right: 0, bottom: 0 }
        : anid.GetBounds(g.m_ani.GetStrip(), g.m_ani.GetFrame());
    prc.left += c.PcFromUwc(pgob.m_wx);
    prc.right += c.PcFromUwc(pgob.m_wx);
    prc.top += c.PcFromUwc(pgob.m_wy);
    prc.bottom += c.PcFromUwc(pgob.m_wy);
    return prc;
  }

  return { left: 0, top: 0, right: 0, bottom: 0 }; // Gob::GetClippingBounds
}

let s_nSeqLastVisible = 0; // GameObjects.cpp:2482 static

/** The per-frame visible-gob DRAW cap (Simulation::m_apgobVisible). The original
 *  C++ was a FIXED array ht.h:5345 kcpgobVisibleMax == 200, so >cap gobs in view
 *  meant the beyond-cap tail in grid-scan order went undrawn and units shuffling
 *  past the boundary blinked in and out each frame. In the TS port the list is a
 *  plain array, so the fixed-size reason is gone; bind the cap to the engine's
 *  ABSOLUTE gob-slot ceiling — sim level.ts kcpgobMax (ht.h:5341), the size of
 *  GobMgr's master slot array — BY REFERENCE, so the two can never drift. No game
 *  can allocate a 769th gob (an authored mission's build limits are bounded by the
 *  same array), so the draw list holds EVERY gob that can exist and NEVER
 *  truncates: no blink, and beyond-cap gobs stay hit-testable, on any map. The
 *  hard-stop below is kept as a pathological-state safety valve, parked far above
 *  any reachable count.
 *
 *  Faithfulness note: EVERY per-frame FindGobs pass funnels through the ONE
 *  FindVisibleGobs() cache (fingerhandler FindVisibleGobs -> renderFindGobs), so
 *  the draw pass and OnUpdateMapInvalidate's redraw-marking pass read the same
 *  list and the cap moves on both together (a larger cap on just ONE pass would
 *  desync them — the wide-window erasure regression). */
export const kcpgobVisibleMax = kcpgobMax;

// game/GameObjects.cpp:2463-2590 — the render FindGobs (pbFogMap is unused in
// the shipped body; signature parity kept by taking the view only)
export function renderFindGobs(
  ctx: GobDrawContext,
  view: SimView,
  rcBounds: Rect,
  cpgobMax = kcpgobVisibleMax,
): RenderGob[] {
  const c = ctx.coords;
  const { map, ctx: mctx, cty: mcty } = view.gobm.gidMapForRender();

  let txLeft = c.TcFromUpc(rcBounds.left);
  let xRight = rcBounds.right + c.gcxTile - 1;
  if (xRight >= kpcMax) xRight = kpcMax - 1;
  let txRight = c.TcFromUpc(xRight);
  let tyTop = c.TcFromUpc(rcBounds.top);
  let yBottom = rcBounds.bottom + c.gcyTile - 1;
  if (yBottom >= kpcMax) yBottom = kpcMax - 1;
  let tyBottom = c.TcFromUpc(yBottom);

  s_nSeqLastVisible = (s_nSeqLastVisible + 1) & 0xffff;

  txLeft = Math.max(0, txLeft - 1);
  txRight = Math.min(mctx, txRight + 1);
  tyTop = Math.max(0, tyTop - 1);
  tyBottom = Math.min(mcty, tyBottom + 1);

  const ctx_ = txRight - txLeft;
  const cty_ = tyBottom - tyTop;
  const out: RenderGob[] = [];

  for (let row = 0; row < cty_; row++) {
    for (let col = 0; col < ctx_; col++) {
      const iCell = (tyTop + row) * mctx + (txLeft + col);
      let gidT = map[iCell];
      while (gidT !== kgidNull) {
        let pgobT: RenderGob;
        let gidNext: number;
        if ((gidT & kwfGidEndMarker) !== 0) {
          pgobT = view.gobm.GetGob(gidT & ~kwfGidEndMarker, false) as RenderGob;
          gidNext = kgidNull;
        } else {
          pgobT = view.gobm.GetGob(gidT, false) as RenderGob;
          gidNext = pgobT.m_gidNext ?? kgidNull;
        }
        let ffGob = pgobT.GetFlags();

        // structure dedup: visit once per FindGobs pass
        if (ffGob & kfGobStructure) {
          const pstru = pgobT as RenderGob & { m_nSeqLastVisible: number };
          if (pstru.m_nSeqLastVisible === s_nSeqLastVisible) break;
          pstru.m_nSeqLastVisible = s_nSeqLastVisible;
        }

        let visible = true;
        // edge cells: clip test
        if (col === 0 || col === ctx_ - 1 || row === 0 || row === cty_ - 1) {
          const rcClip = gobClippingBounds(ctx, pgobT);
          if (
            rcClip.left >= rcBounds.right ||
            rcClip.right <= rcBounds.left ||
            rcClip.top >= rcBounds.bottom ||
            rcClip.bottom <= rcBounds.top
          ) {
            visible = false;
          }
        }

        if (visible) {
          if (!(ffGob & kfGobVisibleLastFrame)) ffGob |= kfGobTransitioningToVisible;
          else ffGob &= ~kfGobTransitioningToVisible;
          ffGob = ((ffGob & ~kfGobIncludeFindVisible) | kfGobVisibleLastFrame) >>> 0;
          pgobT.SetFlags(ffGob);
          out.push(pgobT);
          if (out.length === cpgobMax) {
            SortGobs(ctx, out);
            return out;
          }
        } else {
          ffGob &= ~kfGobTransitioningToVisible;
          if (ffGob & kfGobIncludeFindVisible) {
            pgobT.SetFlags((ffGob & ~(kfGobIncludeFindVisible | kfGobVisibleLastFrame)) >>> 0);
            out.push(pgobT);
            if (out.length === cpgobMax) {
              SortGobs(ctx, out);
              return out;
            }
          } else {
            pgobT.SetFlags((ffGob & ~kfGobVisibleLastFrame) >>> 0);
          }
        }
        gidT = gidNext;
      }
    }
  }

  SortGobs(ctx, out);
  return out;
}

// EDITOR-ONLY terrain override (default null → the shipped game/oracle path is byte-identical).
// The WYSIWYG editor's "Terrain" layer toggle hides PLACED tile features while keeping the tiled
// base background — exactly what a tilemap compiled from the level with its `tile` items stripped
// draws. When set, drawBackground draws THIS background-only tilemap instead of the level's, so the
// single GL renderer can serve terrain-off without a second (Canvas2D) renderer. The editor sets it
// for the one synchronous render call and restores null in a finally, so a concurrent playtest
// render never observes it. Galaxite/fog are gob-ish overlays (drawn below) and stay visible.
let gEditorBgTmap: TileMap | null = null;
export function setEditorBackgroundTmap(tmap: TileMap | null): void {
  gEditorBgTmap = tmap;
}

// EDITOR-ONLY galaxite + wall suppression (default false → shipped game/oracle byte-identical).
// DrawGalaxite paints BOTH galaxite and walls from the terrain-resource map (fogdraw.ts) in the
// background pass — NOT through the per-gob FindVisibleGobs loop — so the editor's "Gobs" layer
// toggle (which empties that loop) can't hide them. The editor sets this from its Gobs toggle so
// galaxite and walls hide with the other gobs, matching the old Canvas2D renderer; restored false in
// a finally so a concurrent playtest render never sees it true.
let gEditorHideMapFeatures = false;
export function setEditorHideMapFeatures(hide: boolean): void {
  gEditorHideMapFeatures = hide;
}

/** game/Simulation.cpp:762-779 — terrain + galaxite where invalidated. */
export function drawBackground(
  ctx: GobDrawContext,
  view: SimView,
  tmap: TileMap,
  fog: ClientFogDraw,
  pupd: UpdateMap,
  pbm: CpuDibBitmap,
): void {
  // Pixel-level scroll: render at the TRUE pixel origin. The old `& 0xfffe` mask
  // snapped the view to even pixels (a faithful port of the WASM client's
  // word-aligned blit), which made the map scroll in 2px steps. Dropping it lets
  // the map scroll one pixel at a time. ALL view-origin sites drop the same mask
  // in lockstep — terrain/fog/gobs here, the scroll-blit delta + gob cull
  // (fingerhandler.ts), and the move marker / minimap box (simuiform.ts) — so the
  // incremental scroll blit stays aligned. NOTE: this is a DELIBERATE divergence
  // from the 2px-snapped WASM client (TS is the smooth-scroll default; WASM is
  // being retired). The pixel-vector oracle is re-pinned TS-only for odd views.
  const xView = ctx.coords.PcFromUwc(view.wxView);
  const yView = ctx.coords.PcFromUwc(view.wyView);
  // The C++ keeps the sub-tile view offset in the UpdateMap via the scroll chain
  // (Simulation::SetViewPos -> UpdateMap::Scroll -> SetMapOffset); a from-scratch
  // frame must set it directly or MapInfo.cxLeftTile=0 drops the last partial
  // tile column/row (a xView%tile-wide undrawn strip at the right/bottom edge)
  pupd.SetMapOffset(xView % ctx.coords.gcxTile, yView % ctx.coords.gcyTile, false);
  const siz = pbm.GetSize();
  (gEditorBgTmap ?? tmap).Draw(pbm, 0, 0, siz.cx, siz.cy, xView, yView, view.fogBytes, pupd);
  if (!gEditorHideMapFeatures) {
    fog.DrawGalaxite(
      pbm,
      xView,
      yView,
      pupd,
      view.fogBytes,
      view.ctxMap,
      view.ctyMap,
      view.trMapBytes,
    );
  }
}

/** game/Simulation.cpp:958-966 — the fog overlay, drawn ON TOP of the gob
 *  layers (SimUI.cpp:1510 draw order: background → gobs → selection → FOG). */
export function drawFog(
  ctx: GobDrawContext,
  view: SimView,
  fog: ClientFogDraw,
  pupd: UpdateMap,
  pbm: CpuDibBitmap,
): void {
  const xView = ctx.coords.PcFromUwc(view.wxView);
  const yView = ctx.coords.PcFromUwc(view.wyView);
  fog.Draw(pbm, xView, yView, pupd, view.fogBytes, view.ctxMap, view.ctyMap);
}

// game/form.cpp:717-737 FillHelper (the pupd arm) — fill the invalid parts of prc
function fillHelper(pbm: CpuDibBitmap, pupd: UpdateMap, prc: Rect, clr: Color): void {
  let fFirst = true;
  for (;;) {
    const rcInvalid = pupd.EnumUpdateRects(fFirst, prc);
    if (rcInvalid === null) break;
    fFirst = false;
    pbm.Fill(
      rcInvalid.left,
      rcInvalid.top,
      rcInvalid.right - rcInvalid.left,
      rcInvalid.bottom - rcInvalid.top,
      clr,
    );
  }
}

/** game/Simulation.cpp:780-957 — the per-layer gob draw over the visible set.
 *  kfPerfSymbolFlashing is pinned ON (lobby forces kfPerfMax).
 *  apgobVisible is the frame's FindVisibleGobs cache (Simulation.cpp:806 —
 *  the SAME m_apgobVisible list OnUpdateMapInvalidate marked for redraw); the
 *  fallback walk serves list-less harness callers only. */
export function simulationDraw(
  ctx: GobDrawContext,
  view: SimView,
  pupd: UpdateMap,
  pbm: CpuDibBitmap,
  apgobVisible?: RenderGob[],
): RenderGob[] {
  const xView = ctx.coords.PcFromUwc(view.wxView);
  const yView = ctx.coords.PcFromUwc(view.wyView);
  pupd.SetViewOrigin(xView, yView);

  const siz = pbm.GetSize();
  const visible =
    apgobVisible ??
    renderFindGobs(ctx, view, {
      left: xView,
      top: yView,
      right: xView + siz.cx,
      bottom: yView + siz.cy,
    });

  let ffLayers = 0;
  for (const g of visible) ffLayers |= g.GetFlags();
  ffLayers &= kfGobLayerMask;

  let ffLayerT = kfGobLayerSurfaceDecal;
  for (let nLayer = knLayerSurfaceDecal; nLayer <= knLayerEnd; nLayer++, ffLayerT <<= 1) {
    if (!(ffLayerT & ffLayers)) continue;
    for (const pgobT of visible) {
      const ff = pgobT.GetFlags();
      if (!(ff & kfGobRedraw)) continue;
      if (!(ff & ffLayerT)) continue;
      dispatchDraw(ctx, pgobT, pbm, xView, yView, nLayer, view);
    }
  }

  for (const pgobT of visible) {
    pgobT.SetFlags((pgobT.GetFlags() & ~kfGobRedraw) >>> 0);
  }

  pupd.SetViewOrigin(0, 0);

  // Simulation.cpp:941-957 — if the screen is larger than the map size we
  // clear those areas to black color (gated by the update map; this is what
  // keeps the dead zone right of / below a smaller-than-surface map clean
  // across scroll blits and dismissed overlay forms)
  const sizMap = {
    cx: view.ctxMap * ctx.coords.gcxTile,
    cy: view.ctyMap * ctx.coords.gcyTile,
  };
  if (sizMap.cx < siz.cx) {
    fillHelper(
      pbm,
      pupd,
      { left: sizMap.cx, top: 0, right: siz.cx, bottom: sizMap.cy },
      GetColor(iclr.kiclrBlack),
    );
  }
  if (sizMap.cy < siz.cy) {
    fillHelper(
      pbm,
      pupd,
      { left: 0, top: sizMap.cy, right: siz.cx, bottom: siz.cy },
      GetColor(iclr.kiclrBlack),
    );
  }
  return visible;
}

/** The C++ virtual Gob::Draw becomes a structural dispatch. */
export function dispatchDraw(
  ctx: GobDrawContext,
  pgob: RenderGob,
  pbm: CpuDibBitmap,
  xView: number,
  yView: number,
  nLayer: number,
  view?: SimView,
): void {
  const ff = pgob.GetFlags();
  const gt = pgob.GetType();
  // projectiles: TankShot/Bullet/ArtilleryShot/AndyShot + Rocket (smoke-fire layer)
  if (
    gt === kgtTankShot ||
    gt === kgtBullet ||
    gt === kgtArtilleryShot ||
    gt === kgtAndyShot ||
    gt === kgtRocket
  ) {
    drawShotGob(ctx, pgob as never, pbm, xView, yView, nLayer, gt === kgtRocket);
    return;
  }
  if (ff & kfGobUnit) {
    if (gt === 35 /* kgtReplicator */) {
      drawReplicatorGob(ctx, pgob as never, pbm, xView, yView, nLayer);
      return;
    }
    if (gt === 21 /* kgtWarehouse */) {
      // QUIRK: this draw MUTATES the sim ani frame from live credits (B-series)
      drawWarehouseGob(ctx, pgob as never, pbm, xView, yView, nLayer);
      return;
    }
    // hidden miners (docked in a processor) draw NOTHING on any layer
    // (MinerGob::Draw's first line, Miner.cpp:147-148)
    if ((pgob as { m_fHidden?: boolean }).m_fHidden === true) return;
    const turret = (pgob as { m_aniTurret?: unknown }).m_aniTurret;
    if (turret !== undefined && !(ff & kfGobStructure)) {
      // tanks/vehicles: base + turret replace the depth-sorted arm
      drawTankGob(ctx, pgob as never, pbm, xView, yView, nLayer);
    } else if (ff & kfGobStructure) {
      // MobileUnitBuilderGob::Draw (MobileBuilder.cpp:22-39): an UPGRADED
      // VTS/HRC draws strips +3 (the upgraded art), restored after — and the
      // in-progress upgrade overlays the galaxite border flash.
      const gUp = pgob as RenderGob & {
        m_puntc?: { fUpgrade?: number; fUpgradeInProgress?: number };
        m_pplr?: { GetUpgrades(): number };
        m_ani?: { GetStrip(): number; SetStrip(n: number): void };
        SetAnimationStrip?(pani: unknown, nStrip: number): void;
      };
      const fUpgrade = gUp.m_puntc?.fUpgrade ?? 0;
      const fUpgraded =
        fUpgrade !== 0 && gUp.m_pplr !== undefined && (gUp.m_pplr.GetUpgrades() & fUpgrade) !== 0;
      // MobileBuilder.cpp:27/35 go through Gob::SetAnimationStrip, which sets
      // kfGobAnimationChanged — paint-fed SP update-cadence state (the same
      // C++ presentation->sim feedback as the miner vacuum; see drawMinerVacuum).
      if (fUpgraded) gUp.SetAnimationStrip!(gUp.m_ani!, gUp.m_ani!.GetStrip() + 3);
      // StructGob::Draw: the minimap arm + symbols + the being-built bar over
      // the shared unit arms
      drawStructGob(ctx, pgob as never, pbm, xView, yView, nLayer);
      if (turret !== undefined) {
        drawTowerTurret(ctx, pgob as never, pbm, xView, yView, nLayer);
      }
      if (
        nLayer === 1 /* knLayerDepthSorted */ &&
        view?.plrLocal &&
        (gUp.m_puntc?.fUpgradeInProgress ?? 0) !== 0 &&
        gUp.m_pplr !== undefined &&
        (gUp.m_pplr.GetUpgrades() & gUp.m_puntc!.fUpgradeInProgress!) !== 0
      ) {
        drawUpgradeEffect(ctx, pgob as never, view.plrLocal, pbm, xView, yView);
      }
      if (fUpgraded) gUp.SetAnimationStrip!(gUp.m_ani!, gUp.m_ani!.GetStrip() - 3);
      // ResearchGob::Draw (Research.cpp:387-400): the upgrade progress bar
      if (
        (pgob as RenderGob & { m_utTarget?: number }).m_utTarget !== undefined &&
        view !== undefined
      ) {
        drawResearchUpgradeProgress(
          ctx,
          pgob as never,
          view.apuntc as never,
          pbm,
          xView,
          yView,
          nLayer,
        );
      }
    } else {
      // non-structure units without a turret (infantry): the shared unit arm
      drawUnitGob(ctx, pgob as never, pbm, xView, yView, nLayer);
    }
    // processors: docked-miner + processing overlay (Processor.cpp:150-184)
    if ((pgob as { m_aniOverlay?: unknown }).m_aniOverlay !== undefined) {
      drawProcessorOverlay(ctx, pgob as never, pbm, xView, yView, nLayer);
    }
    // miners: the vacuum overlay while sucking (Miner.cpp:150-159)
    if ((pgob as { m_aniVacuum?: unknown }).m_aniVacuum !== undefined) {
      drawMinerVacuum(ctx, pgob as never, pbm, xView, yView, nLayer);
    }
    // builders overlay their build-progress bar (Builder.cpp:343-356)
    const bq = (pgob as { m_bq?: { IsEmpty(): boolean } }).m_bq;
    if (bq !== undefined && view?.plrLocal) {
      drawBuilderProgress(
        ctx,
        pgob as never,
        view.plrLocal,
        view.apuntc,
        pbm,
        xView,
        yView,
        nLayer,
      );
    }
  } else if (gt === kgtScenery) {
    drawSceneryGob(ctx, pgob as never, pbm, xView, yView, nLayer);
  } else if (gt === kgtSurfaceDecal || gt === kgtScorch) {
    drawSurfaceDecalGob(ctx, pgob as never, pbm, xView, yView, nLayer);
  } else if (
    gt === kgtAnimation ||
    gt === kgtSmoke ||
    gt === kgtPuff ||
    (pgob as { m_wfAnm?: number }).m_wfAnm !== undefined
  ) {
    drawAnimGob(ctx, pgob as never, pbm, xView, yView, nLayer);
  }
}
